import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type {
  IFilterComp,
  IDoesFilterPassParams,
  IFilterParams,
  FilterModel,
} from "ag-grid-community";

type Option = { value: string; label: string };
type FilterMode = "dropdown" | "checkbox";

type OptionFilterProps = IFilterParams & {
  options: Option[];
  valueGetter?: (params: any) => any;
  mode?: FilterMode;
  onValueChange?: (value: string | string[]) => void;
};

type OptionFilterModel = FilterModel & {
  filterType?: string;
  values?: string[];
};

const FILTER_TYPE = "optionFilter";

const OptionFilter = forwardRef<IFilterComp, OptionFilterProps>(
  (props, ref) => {
    const { options, mode = "dropdown", onValueChange } = props;
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectedRef = useRef<string[]>([]);

    const isActive = selectedRef.current.length > 0;

    const updateSelection = (nextValues: string[]) => {
      selectedRef.current = nextValues;
      setSelectedValues(nextValues);
      onValueChange?.(mode === "dropdown" ? (nextValues[0] ?? "") : nextValues);
      props.filterChangedCallback?.();
    };

    useImperativeHandle(ref, () => ({
      isFilterActive: () => isActive,
      doesFilterPass: (params: IDoesFilterPassParams) => {
        if (!isActive) return true;
        const { colDef, valueGetter } = props;
        const valueFromRow = valueGetter
          ? valueGetter(params.node)
          : params.data?.[colDef.field as string];
        if (valueFromRow === undefined || valueFromRow === null) return false;
        const normalized = Array.isArray(valueFromRow)
          ? valueFromRow.map((v) => String(v))
          : [String(valueFromRow)];
        return normalized.some((v) => selectedRef.current.includes(v));
      },
      getModel: () =>
        isActive
          ? ({
              filterType: FILTER_TYPE,
              values: selectedRef.current,
            } as OptionFilterModel)
          : null,
      setModel: (model?: OptionFilterModel | null) => {
        if (model?.values && model.values.length) {
          updateSelection(model.values);
        } else {
          updateSelection([]);
        }
      },
      getModelAsString: () => selectedRef.current.join(", "),
      afterGuiAttached: () => {},
      getGui: () => containerRef.current ?? document.createElement("div"),
    }));

    const handleSelectChange = (
      event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      const value = event.target.value;
      updateSelection(value ? [value] : []);
    };

    const handleCheckboxToggle = (value: string) => {
      const next = selectedRef.current.includes(value)
        ? selectedRef.current.filter((v) => v !== value)
        : [...selectedRef.current, value];
      updateSelection(next);
    };

    const renderDropdown = () => (
      <select
        value={selectedValues[0] ?? ""}
        onChange={handleSelectChange}
        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
      >
        <option value="">Tất cả</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );

    const renderCheckboxes = () => (
      <div className="space-y-2">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-center gap-2 text-sm text-gray-700"
          >
            <input
              type="checkbox"
              checked={selectedValues.includes(opt.value)}
              onChange={() => handleCheckboxToggle(opt.value)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <span>{opt.label}</span>
          </label>
        ))}
        {options.length === 0 && (
          <span className="text-xs text-gray-500">No options</span>
        )}
      </div>
    );

    return (
      <div style={{ padding: "8px" }} ref={containerRef}>
        {mode === "checkbox" ? renderCheckboxes() : renderDropdown()}
      </div>
    );
  },
);

OptionFilter.displayName = "OptionFilter";

export default OptionFilter;
export type { Option, FilterMode, OptionFilterProps, OptionFilterModel };
